// Shared vehicle inspection primitives: checklists + top-view damage diagrams (car / motorcycle).
// Used by the handover form (interactive), receipts and portals (read-only).

export const CHECK_ITEMS = [
  { key: "seats", label: "المقاعد الداخلية" },
  { key: "tires", label: "الإطارات" },
  { key: "wheel_cover", label: "غطاء العجلة الخارجية" },
  { key: "spare_tire", label: "الإطار الاحتياطي" },
  { key: "fire_ext", label: "طفاية الحريق" },
  { key: "safety_triangle", label: "مثلث الأمان" },
  { key: "ac", label: "المكيّف" },
  { key: "wipers", label: "مساحات الزجاج" },
  { key: "interior_lights", label: "الإنارة الداخلية" },
  { key: "rear_lights", label: "المصابيح الخلفية" },
  { key: "turn_signals", label: "إشارات الانعطاف" },
  { key: "brake_lights", label: "أضواء الفرامل" },
  { key: "mirrors", label: "المرايا" },
  { key: "wrench", label: "مفتاح البراغي / العدّة" },
  { key: "jack", label: "الرافعة (الجك)" },
  { key: "docs", label: "أوراق المركبة (الاستمارة)" },
];

export const MOTO_ITEMS = [
  { key: "tires", label: "الإطارات" },
  { key: "front_light", label: "المصباح الأمامي" },
  { key: "rear_lights", label: "المصباح الخلفي" },
  { key: "turn_signals", label: "إشارات الانعطاف" },
  { key: "brake_lights", label: "أضواء الفرامل" },
  { key: "brakes", label: "الفرامل" },
  { key: "mirrors", label: "المرايا" },
  { key: "horn", label: "البوري" },
  { key: "chain", label: "الجنزير" },
  { key: "moto_seat", label: "المقعد" },
  { key: "delivery_box", label: "صندوق التوصيل" },
  { key: "helmet", label: "الخوذة" },
  { key: "stand", label: "الستاند" },
  { key: "plate", label: "اللوحة" },
  { key: "keys", label: "المفتاح" },
  { key: "docs", label: "أوراق المركبة (الاستمارة)" },
];

export const itemsFor = (kind) => (kind === "moto" ? MOTO_ITEMS : CHECK_ITEMS);
export const CHECK_LABEL = Object.fromEntries([...CHECK_ITEMS, ...MOTO_ITEMS].map((i) => [i.key, i.label]));
export const labelsFor = (kind) => Object.fromEntries(itemsFor(kind).map((i) => [i.key, i.label]));

// Inline SVG strings (viewBox 0 0 200 320) — reused by the React diagram and printed receipts.
export const CAR_SVG_INNER = `
  <text x="100" y="12" text-anchor="middle" font-size="11" fill="#94a3b8">الأمام</text>
  <text x="100" y="314" text-anchor="middle" font-size="11" fill="#94a3b8">الخلف</text>
  <rect x="45" y="20" width="110" height="280" rx="38" fill="#f8fafc" stroke="#cbd5e1" stroke-width="2" />
  <path d="M60 70 Q100 50 140 70 L132 100 Q100 90 68 100 Z" fill="#e2e8f0" />
  <path d="M68 240 Q100 250 132 240 L140 268 Q100 285 60 268 Z" fill="#e2e8f0" />
  <rect x="66" y="112" width="68" height="116" rx="14" fill="#eef2f7" stroke="#cbd5e1" />
  <rect x="34" y="60" width="12" height="34" rx="4" fill="#334155" />
  <rect x="154" y="60" width="12" height="34" rx="4" fill="#334155" />
  <rect x="34" y="226" width="12" height="34" rx="4" fill="#334155" />
  <rect x="154" y="226" width="12" height="34" rx="4" fill="#334155" />`;

export const MOTO_SVG_INNER = `
  <text x="100" y="12" text-anchor="middle" font-size="11" fill="#94a3b8">الأمام</text>
  <text x="100" y="316" text-anchor="middle" font-size="11" fill="#94a3b8">الخلف</text>
  <rect x="88" y="20" width="24" height="56" rx="12" fill="#334155" />
  <rect x="40" y="78" width="120" height="9" rx="4.5" fill="#64748b" />
  <circle cx="46" cy="68" r="8" fill="#e2e8f0" stroke="#cbd5e1" />
  <circle cx="154" cy="68" r="8" fill="#e2e8f0" stroke="#cbd5e1" />
  <rect x="91" y="70" width="18" height="10" rx="3" fill="#fde68a" stroke="#f59e0b" />
  <rect x="68" y="92" width="64" height="72" rx="30" fill="#e2e8f0" stroke="#cbd5e1" stroke-width="2" />
  <rect x="78" y="160" width="44" height="64" rx="18" fill="#eef2f7" stroke="#cbd5e1" stroke-width="2" />
  <rect x="52" y="176" width="16" height="7" rx="3" fill="#94a3b8" />
  <rect x="132" y="176" width="16" height="7" rx="3" fill="#94a3b8" />
  <rect x="56" y="226" width="88" height="46" rx="6" fill="#f8fafc" stroke="#94a3b8" stroke-width="2" stroke-dasharray="5 3" />
  <text x="100" y="254" text-anchor="middle" font-size="10" fill="#94a3b8">الصندوق</text>
  <rect x="88" y="274" width="24" height="30" rx="10" fill="#334155" />`;

export const svgFor = (kind) => (kind === "moto" ? MOTO_SVG_INNER : CAR_SVG_INNER);

export const isMotoText = (s) => /دراج|دباب|موتو|moto|bike/i.test(String(s || ""));

export const CarDamageDiagram = ({ damages = [], interactive = false, onAdd, onRemove, diagramRef, kind = "car" }) => (
  <div
    ref={diagramRef}
    onClick={interactive ? onAdd : undefined}
    data-testid="insp-damage-diagram"
    className={`relative mx-auto w-full max-w-[280px] select-none rounded-lg border border-slate-200 bg-white p-2 ${interactive ? "cursor-crosshair" : ""}`}
  >
    <svg viewBox="0 0 200 320" className="w-full h-auto pointer-events-none"
      dangerouslySetInnerHTML={{ __html: svgFor(kind) }} />
    {damages.map((d, i) => (
      <button key={i} type="button" data-testid={`insp-marker-${i}`}
        onClick={interactive ? (e) => { e.stopPropagation(); onRemove?.(i); } : undefined}
        style={{ left: `${d.x}%`, top: `${d.y}%`, cursor: interactive ? "pointer" : "default" }}
        className="absolute -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-rose-600 text-white text-xs font-bold grid place-items-center shadow ring-2 ring-white">
        {i + 1}
      </button>
    ))}
  </div>
);
